import { inject, Injectable } from '@angular/core';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import {
  PortfolioCustomMetricDTO,
  XcdService
} from '@libs/api';
import { Store } from '@ngrx/store';
import { AppState } from '@portal/app/store/app.state';
import { DashboardActions } from '@portal/app/store/dashboard/actions';
import { PersistenceRequest } from '@portal/app/shared/types/native/layout';
import { cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class PortfolioCustomMetricsService {
  private readonly api = inject(XcdService);
  private readonly selectionService = inject(SelectionService);
  private readonly store = inject<Store<AppState>>(Store as never);

  getPortfolioCustomMetrics(
    conversionTypeId?: number | null
  ): Observable<PortfolioCustomMetricDTO[]> {
    if (!conversionTypeId) {
      return of([]);
    }
    const { clientId, brandId } = this.selectionService.getClientIdAndBrandId();
    if (!clientId || !brandId) {
      return of([]);
    }
    return this.api
      .portfolioCustomMetricsControllerGetPortfolioCustomMetrics(
        clientId,
        brandId,
        conversionTypeId
      )
      .pipe(catchError(() => of([] as PortfolioCustomMetricDTO[])));
  }

  fetchAndStorePortfolioCustomMetrics(
    conversionTypeId: number | null,
    dashboardLiteralId?: string
  ): Observable<void> {
    if (dashboardLiteralId !== 'cross-channel' || !conversionTypeId) {
      this.store.dispatch(
        DashboardActions.setPortfolioCustomMetrics({
          portfolioCustomMetrics: []
        })
      );
      return of(undefined);
    }

    return this.getPortfolioCustomMetrics(conversionTypeId).pipe(
      take(1),
      map((metrics) => {
        this.store.dispatch(
          DashboardActions.setPortfolioCustomMetrics({
            portfolioCustomMetrics: metrics
          })
        );
      }),
      catchError(() => {
        this.store.dispatch(
          DashboardActions.setPortfolioCustomMetrics({
            portfolioCustomMetrics: []
          })
        );
        return of(undefined);
      })
    );
  }

  removeCustomMetricsFromPersistenceRequest(
    persistenceRequest: PersistenceRequest
  ): PersistenceRequest {
    const customMetricIds = this.getCustomMetricLiteralIds();
    if (!customMetricIds.length) {
      return persistenceRequest;
    }
    const sanitizedRequest = cloneDeep(persistenceRequest);
    sanitizedRequest.data =
      sanitizedRequest.data?.filter(
        (data: { dashboardFieldId?: string }) =>
          !data.dashboardFieldId ||
          !customMetricIds.includes(data.dashboardFieldId)
      ) || [];
    return sanitizedRequest;
  }

  getCustomMetricLiteralIds(): string[] {
    const state = (
      this.store as Store<AppState> & { source?: { value?: AppState } }
    ).source?.value;
    const metrics = state?.dashboard?.portfolioCustomMetrics ?? [];
    return metrics.map((m) => m.literalId).filter(Boolean) as string[];
  }
}
